import React, { useEffect } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import { palette } from '../../theme/colors';

interface Quote {
  id: string;
  quoteNumber: string;
  status: string;
  total: number;
  validUntil: string;
  createdAt: string;
  items: Array<{
    productName: string;
    quantity: number;
    unitPrice: number;
  }>;
}

interface PortalContext {
  isAuthenticated: boolean;
}

const fetchMyQuotes = async (orgSlug: string, token: string): Promise<Quote[]> => {
  const response = await fetch(
    `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/b2b-portal/${orgSlug}/my-quotes`,
    {
      headers: { 'Authorization': `Bearer ${token}` },
    }
  );
  if (!response.ok) {
    throw new Error('Failed to fetch quotes');
  }
  const data = await response.json();
  return Array.isArray(data) ? data : data.data || [];
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'accepted':
      return palette.success;
    case 'sent':
      return palette.primary;
    case 'draft':
      return palette.textMuted;
    case 'rejected':
    case 'expired':
      return palette.error;
    default:
      return palette.warning;
  }
};

export const B2BQuotesPage: React.FC = () => {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useOutletContext<PortalContext>();
  const token = localStorage.getItem('access_token');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate(`/b2b/${orgSlug}/login`);
    }
  }, [isAuthenticated, navigate, orgSlug]);

  const { data: quotes, isLoading, error } = useQuery({
    queryKey: ['b2b-my-quotes', orgSlug],
    queryFn: () => fetchMyQuotes(orgSlug!, token!),
    enabled: !!orgSlug && !!token,
  });

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        <RequestQuoteIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        My Quotes
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        View and track your quote requests
      </Typography>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          Failed to load quotes. Please try again.
        </Alert>
      )}

      {!isLoading && quotes?.length === 0 && (
        <Card sx={{ textAlign: 'center', py: 8 }}>
          <RequestQuoteIcon sx={{ fontSize: 80, color: palette.textMuted, mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No quotes yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Request quotes from the product catalog
          </Typography>
        </Card>
      )}

      {quotes && quotes.length > 0 && (
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: palette.sky }}>
                  <TableCell sx={{ fontWeight: 600 }}>Quote #</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Items</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Valid Until</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {quotes.map((quote) => (
                  <TableRow
                    key={quote.id}
                    hover
                    onClick={() => navigate(`/b2b/${orgSlug}/quotes/${quote.id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography fontWeight={600}>{quote.quoteNumber}</Typography>
                    </TableCell>
                    <TableCell>
                      {quote.items.length} item{quote.items.length !== 1 ? 's' : ''}
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={600} color={palette.primary}>
                        {quote.total.toFixed(2)} DZD
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {new Date(quote.validUntil).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={quote.status.toUpperCase()}
                        size="small"
                        sx={{
                          bgcolor: `${getStatusColor(quote.status)}20`,
                          color: getStatusColor(quote.status),
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(quote.createdAt).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
    </Box>
  );
};
